import * as React from 'react';
import {faCalendarAlt, faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {forwardRef, useImperativeHandle, useLayoutEffect, useRef} from 'react';

import {dateObjectToString} from '../../../tools/date.functions';
import '../../../../client/ui/vendor/tiny-date-picker.scss';

interface DatePickerProps {
    autoFocus?: boolean;
    className?: string;
    error?: string;
    label: string;
    min?: string | number;
    name: string;
    onChange?: (pickerState: {target: HTMLInputElement} | string) => void;
    ref?: any;
    value?: string;
}

const DatePicker: React.FC<DatePickerProps> = forwardRef(
    ({autoFocus = false, className, error, label, min, name, onChange = () => null, value}, ref) => {
        const datePickerRef = useRef<HTMLInputElement>();
        const tinyDatePickerRef = useRef<any>(null);

        useImperativeHandle(ref, () => datePickerRef.current, []);

        // Build the date format for input
        const dateHelper = (originalDate: string | Date) => {
            if (!originalDate) {
                return null;
            }
            const date = new Date(originalDate);
            return dateObjectToString(date);
        };

        useLayoutEffect(() => {
            if (datePickerRef.current) {
                const TinyDatePicker = require('tiny-date-picker');
                tinyDatePickerRef.current = TinyDatePicker(datePickerRef.current, {
                    appendTo: datePickerRef.current,
                    mode: `dp-below`,
                    min,
                    format: (date: string) => dateHelper(date),
                });
                tinyDatePickerRef.current.on(`statechange`, (_, picker) => {
                    if (picker.state.hilightedDate === picker.state.selectedDate) {
                        const originalDate: Date = picker.state.selectedDate;
                        datePickerRef.current.value = dateHelper(originalDate);
                        onChange({target: datePickerRef.current});
                    }
                });
                if (autoFocus) {
                    datePickerRef.current.focus();
                }
                return () => {
                    tinyDatePickerRef.current.close();
                    tinyDatePickerRef.current.destroy();
                };
            }
        }, [autoFocus, min, onChange]);

        /**
         * Template
         */
        return (
            <div className={className}>
                <div className="mb-0 floating input-group">
                    <input
                        autoComplete="off"
                        className={`form-control float${error ? ' error' : ''}`}
                        defaultValue={dateHelper(value)}
                        id={name}
                        name={name}
                        onInput={(e) => {
                            e.currentTarget.value = dateHelper(e.currentTarget.value);
                            onChange(e.currentTarget.value);
                        }}
                        ref={datePickerRef}
                    />
                    {error && (
                        <FontAwesomeIcon
                            className={`error ${error ? 'dedent mr-3' : ''}`}
                            icon={faExclamationCircle}
                            size="lg"
                        />
                    )}
                    <label
                        className="required form-label"
                        htmlFor={name}
                    >
                        {label}
                    </label>
                    <div className="input-group-append">
                        <button
                            className="bg-white input-group-text pointer"
                            onMouseDown={() => {
                                datePickerRef.current.focus();
                            }}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                        </button>
                    </div>
                </div>
                {error && <label className="form-label error">{error}</label>}
            </div>
        );
    },
);

export default DatePicker;
